import { Chip, Divider, tv } from "@heroui/react";
import { Careprovider } from "core/types";
import { DropdownNavbar, DropdownNavbarMenuItem, Link } from "ds/ui";
import { useFocusElement } from "dsl/hooks/useFocusElement";
import { useLocation, useMatch } from "react-router-dom";
import { useLoggedInAccount } from "reduxentities/hooks";
import { useTranslations } from "translations";
import { useReceiverMenuItems } from "./menuItems";

const settingsSidePanelVariants = tv({
  defaultVariants: {
    active: false,
  },
  slots: {
    aside:
      "hidden desktop:block desktop:self-start sticky top-[var(--navbar-height)] min-h-[calc(100vh-var(--navbar-height))] w-[248px] divide-divider-400 border-r bg-white p-4 pt-7",
    badge: "ml-auto flex-shrink-0",
    iconWrap: "mr-3",
    link: "hover:bg-secondary-light hover:border-l-4 hover:border-primary hover:pl-3 flex flex-row items-center w-full h-9 rounded-r-lg px-4 cursor-pointer data-[focus-visible]:outline-2 data-[focus-visible]:outline-primary data-[focus-visible]:outline-offset-[-1px] data-[focus-visible]:bg-secondary-light data-[focus-visible]:font-semibold justify-between gap-2 text-black hover:no-underline font-medium",
    menuItem: "flex items-center text-body-md rounded-r-lg",
    menuList: "flex flex-col gap-1",
    titleSectionEmail: "text-wrap break-words text-body-sm",
    titleSectionHeading: "outline-none text-heading-xl font-medium",
    titleSectionWrapper: "flex flex-col gap-1 pb-3 desktop:px-3",
  },
  variants: {
    active: {
      true: {
        link: "bg-secondary font-semibold data-[focus-visible]:bg-secondary border-l-4 border-primary pl-3",
      },
    },
  },
});

export const NewChip = () => {
  const translations = useTranslations();

  return (
    <Chip classNames={{ base: "bg-accent-dark text-white" }} size="sm">
      {translations.general.new}
    </Chip>
  );
};

function SettingsMenuItem({
  href,
  hrefMatch,
  icon,
  id,
  label,
  onPress,
  showNewChip,
  tour,
}: DropdownNavbarMenuItem) {
  const isActive = useMatch(hrefMatch ?? "xxx");
  const { iconWrap, link, menuItem } = settingsSidePanelVariants({
    active: !!isActive,
  });

  return (
    <li className={menuItem()}>
      <Link
        className={link()}
        href={href}
        id={`settings-${id}`}
        size="sm"
        onPress={onPress}
        {...tour}
      >
        <div className="flex items-center gap-2">
          <div className={iconWrap()}>{icon}</div>
          <span>{label}</span>
        </div>
        {showNewChip && <NewChip />}
      </Link>
    </li>
  );
}

const NavigationHeader = () => {
  const translations = useTranslations();
  const loggedAccount = useLoggedInAccount();
  const location = useLocation();

  const focusRef = useFocusElement<HTMLHeadingElement>({
    dependencies: [location.pathname],
    setTabIndex: true,
    shouldFocus: true,
  });

  const { titleSectionEmail, titleSectionHeading, titleSectionWrapper } =
    settingsSidePanelVariants();

  return (
    <div className={titleSectionWrapper()}>
      <h1 ref={focusRef as any} className={titleSectionHeading()}>
        {translations.careproviderApp.settings.sidebar.title}
      </h1>
      {loggedAccount?.email && (
        <p className={titleSectionEmail()}>{loggedAccount.email}</p>
      )}
    </div>
  );
};

export function SettingsSidePanelNavigation({
  careprovider,
}: {
  careprovider: Careprovider;
}) {
  const translations = useTranslations();
  const { menuItemsBottom, menuItemsTop } = useReceiverMenuItems({
    careprovider,
  });

  const { aside, menuList } = settingsSidePanelVariants();

  return (
    <aside className={aside()}>
      <nav aria-label={translations.careproviderApp.settings.sidebar.title}>
        <NavigationHeader />
        <Divider aria-hidden="true" className="mb-3" role="presentation" />
        <ul
          aria-label={translations.careproviderApp.settings.topSectionAriaLabel}
          className={menuList()}
          role="navigation"
        >
          {/* Top Menu Section */}
          {menuItemsTop.map((item) => {
            return <SettingsMenuItem key={item.id} {...item} />;
          })}
        </ul>
        {/* Divider between Top and Bottom Menu Sections*/}
        {menuItemsBottom.length > 0 && (
          <Divider aria-hidden="true" className="my-4" role="presentation" />
        )}
        {/* Bottom Menu Section*/}
        <ul
          aria-label={
            translations.careproviderApp.settings.bottomSectionAriaLabel
          }
          className={menuList()}
        >
          {menuItemsBottom.map((item) => {
            return <SettingsMenuItem key={item.id} {...item} />;
          })}
        </ul>
      </nav>
    </aside>
  );
}

export function SettingsMobileNavigation({
  careprovider,
}: {
  careprovider: Careprovider;
}) {
  const translations = useTranslations();
  const { menuItemsTop } = useReceiverMenuItems({
    careprovider,
    isMobileNavigation: true,
  });

  return (
    <div className="block pb-3 desktop:hidden">
      <nav aria-label={translations.careproviderApp.settings.sidebar.title}>
        <NavigationHeader />
        <DropdownNavbar
          id="settings-navigation"
          items={menuItemsTop}
          trackingEventName="DROPDOWN_SETTINGS_ITEM_CLICKED"
        />
      </nav>
    </div>
  );
}
